/**
 * 画布尺寸（DESIGN.md §6.5 补充：「图像→画布大小」，参照 Photoshop）。
 *
 * 跟裁剪（`app/crop.ts`）同一条落地通道（结构命令 + 全图层像素平移，
 * 一条历史）——区别只是 offset 从哪来：裁剪来自用户拖的矩形，这里来自
 * 3×3 锚点公式。新画布比旧的大：多出来的区域透明；比旧的小：超出部分
 * 被裁掉——跟真实 PS「画布大小」完全一致。
 */
import { shiftAllLayerPixels } from './crop';
import { createSetCanvasSizeCommand } from './commands/canvas-commands';
import type { AppStore } from './store';

/** 3×3 锚点，跟真实 PS「画布大小」对话框里的九宫格一一对应。 */
export type CanvasAnchor =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'middle-left'
  | 'center'
  | 'middle-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

/** 锚点 → (fx, fy)：旧画布上跟新画布对齐的那个点，用小数坐标表示（0=左/上，1=右/下）。 */
const ANCHOR_FRACTIONS: Readonly<Record<CanvasAnchor, { readonly fx: number; readonly fy: number }>> = {
  'top-left': { fx: 0, fy: 0 },
  'top-center': { fx: 0.5, fy: 0 },
  'top-right': { fx: 1, fy: 0 },
  'middle-left': { fx: 0, fy: 0.5 },
  center: { fx: 0.5, fy: 0.5 },
  'middle-right': { fx: 1, fy: 0.5 },
  'bottom-left': { fx: 0, fy: 1 },
  'bottom-center': { fx: 0.5, fy: 1 },
  'bottom-right': { fx: 1, fy: 1 },
};

/**
 * 按锚点算出 `shiftLayerPixels` 要的 offset：新画布(0,0) 应该读旧画布的
 * 哪个像素。锚点是"旧内容固定在新画布的哪个角/边/中心"，例如锚点
 * top-left 时新旧画布的 (0,0) 重合，offset=0；锚点 bottom-right 时新旧
 * 画布的右下角重合，offset = 旧尺寸 − 新尺寸（新画布更大时 offset 为负，
 * 内容整体往右下让出左上的新增空间，读数时正确地从"更靠左上"的负坐标
 * 开始，从而在结果里把旧内容摆到右下角）。
 */
export function canvasSizeOffset(
  oldSize: { readonly width: number; readonly height: number },
  newSize: { readonly width: number; readonly height: number },
  anchor: CanvasAnchor,
): { readonly x: number; readonly y: number } {
  const { fx, fy } = ANCHOR_FRACTIONS[anchor];
  // `|| 0` 只是去掉 -0（fx=0 时 0 * 负数 = -0，语义上就是 0，`===`/`toEqual`
  // 会把它跟 +0 区分开，属于纯粹的浮点表示怪癖，不是真的"往负方向挪了一点"
  return {
    x: Math.round(fx * (oldSize.width - newSize.width)) || 0,
    y: Math.round(fy * (oldSize.height - newSize.height)) || 0,
  };
}

/**
 * 改变画布尺寸，旧内容按锚点对齐。返回是否执行
 * （无文档/尺寸非法/尺寸与锚点都不产生变化时 false）。
 */
export function resizeCanvas(
  store: AppStore,
  width: number,
  height: number,
  anchor: CanvasAnchor,
): boolean {
  const document = store.document;
  if (document === null) return false;
  const newWidth = Math.round(width);
  const newHeight = Math.round(height);
  if (newWidth < 1 || newHeight < 1) return false;
  if (newWidth === document.size.width && newHeight === document.size.height) return false;

  const { x, y } = canvasSizeOffset(document.size, { width: newWidth, height: newHeight }, anchor);

  const transaction = store.beginPixelTransaction('画布大小');
  const dirty = shiftAllLayerPixels(store.tiles, document.root.children, x, y, newWidth, newHeight, transaction);

  store.setSelection(null); // 选区坐标随画布改尺寸失义，直接取消（跟裁剪同一个理由）
  store.dispatchWithPixels(createSetCanvasSizeCommand(newWidth, newHeight), transaction.end());
  store.invalidatePixels(dirty);
  return true;
}
